/**
 * Horse and donkey (Bedrock 1.21 stats): random health 15-30, speed
 * 0.1125-0.3375, jump strength 0.4-1.0, seven coats x five markings (horse),
 * taming by mounting with a temper counter, saddle + armour slots, breeding
 * (tamed only) with golden apples / carrots. DOM-free; riding itself lives in
 * game/mounts.ts.
 */

import { I } from '../../game/itemIds';
import { stackOf, type Stack } from '../../game/types';
import { Animal } from '../animals';
import type { MobCtx } from '../mob';

export const MAX_TEMPER = 100;
export const COAT_NAMES = ['white', 'creamy', 'chestnut', 'brown', 'black', 'gray', 'darkbrown'] as const;
export const MARKING_NAMES = ['none', 'white', 'whitefield', 'whitedots', 'blackdots'] as const;
/** Horse armour items by tier 1..4 and the armour points they give. */
export const HORSE_ARMOR = [0, I.LEATHER_HORSE_ARMOR, I.IRON_HORSE_ARMOR, I.GOLDEN_HORSE_ARMOR, I.DIAMOND_HORSE_ARMOR] as const;
export const HORSE_ARMOR_POINTS = [0, 3, 5, 7, 11] as const;

export const armorTierOf = (id: number): number => HORSE_ARMOR.indexOf(id as never);

/** Vanilla generateMaxHealth: 15 + d8 + d9 (15..30). */
export const rollHealth = (rnd: () => number): number => 15 + Math.floor(rnd() * 8) + Math.floor(rnd() * 9);
/** Vanilla generateSpeed attribute: (0.45 + 3 x 0.3r) x 0.25 (0.1125..0.3375). */
export const rollSpeed = (rnd: () => number): number => (0.45 + rnd() * 0.3 + rnd() * 0.3 + rnd() * 0.3) * 0.25;
/** Vanilla generateJumpStrength: 0.4 + 3 x 0.2r (0.4..1.0). */
export const rollJump = (rnd: () => number): number => 0.4 + rnd() * 0.2 + rnd() * 0.2 + rnd() * 0.2;

/** Jump bar fill after `ticks` of holding jump (vanilla: ramps to 1 in 10 ticks, then eases to 0.8). */
export const chargeScale = (ticks: number): number => (ticks <= 0 ? 0 : ticks < 10 ? ticks * 0.1 : 0.8 + (2 / (ticks - 9)) * 0.1);
/** Share of the jump strength used at a bar fill (>= 90% is the full jump). */
export const jumpPower = (scale: number): number => (scale >= 0.9 ? 1 : 0.4 + (0.4 * scale) / 0.9);
/** Launch speed (blocks/tick) of a jump: strength x power. Peak height ~ 5.3 blocks at strength 1.0 (the 0.95 trims our integrator to vanilla's measured heights). */
export const jumpVelocity = (strength: number, scale: number): number => strength * jumpPower(scale) * 0.95;
/** Chance an untamed horse accepts its rider (vanilla RunAroundLikeCrazyGoal: nextInt(max) < temper). */
export const tameChance = (temper: number, max = MAX_TEMPER): number => (max > 0 ? Math.min(1, Math.max(0, temper / max)) : 0);

interface Food { heal: number; temper: number; love: boolean }
const FOODS = new Map<number, Food>([
  [I.SUGAR, { heal: 1, temper: 3, love: false }],
  [I.WHEAT, { heal: 2, temper: 3, love: false }],
  [I.APPLE, { heal: 3, temper: 3, love: false }],
  [I.GOLDEN_CARROT, { heal: 4, temper: 5, love: true }],
  [I.GOLDEN_APPLE, { heal: 10, temper: 10, love: true }],
]);
export const isHorseFood = (id: number): boolean => FOODS.has(id);

export class Horse extends Animal {
  protected raw = 0;
  protected cooked = 0;
  coat = 0;
  markings = 0;
  tame = false;
  temper = 0;
  jumpStrength = 0.7;
  /** Movement-speed attribute (0.1125..0.3375); the ridden speed scales with it. */
  hSpeed = 0.225;
  armorTier = 0;
  readonly donkey: boolean;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number, type: 'horse' | 'donkey' = 'horse') {
    super(id, type, x, y, z, { width: 1.3965, height: type === 'donkey' ? 1.5 : 1.6, eye: type === 'donkey' ? 1.4 : 1.45, health: 20, speed: 0.35 }, rnd);
    this.donkey = type === 'donkey';
    this.stepHeight = 1.0;
    this.setMaxHealth(rollHealth(rnd));
    this.hSpeed = rollSpeed(rnd);
    this.jumpStrength = rollJump(rnd);
    if (!this.donkey) { this.coat = Math.floor(rnd() * 7); this.markings = Math.floor(rnd() * 5); }
    this.syncLook();
  }

  setMaxHealth(h: number): void {
    (this as unknown as { maxHealth: number }).maxHealth = h;
    this.health = h;
  }
  private syncLook(): void {
    this.view.coat = this.donkey ? 8 : this.coat | (this.markings << 3);
    this.view.coat = (this.view.coat ?? 0) | (this.armorTier << 8);
  }
  override syncView(host: Parameters<Animal['syncView']>[0]): void { super.syncView(host); this.syncLook(); }

  get canBeRidden(): boolean { return !this.baby; }
  get canFallInLove(): boolean { return this.tame && super.canFallInLove && this.health >= this.maxHealth; }

  /** Armour reduces damage like player armour points do. */
  override hurt(amount: number): boolean {
    if (this.armorTier > 0) amount *= 1 - HORSE_ARMOR_POINTS[this.armorTier] / 25;
    return super.hurt(amount);
  }

  /** Feed `item`: heals, raises temper, may start love. Returns whether it was eaten. */
  feed(item: number, ctx?: { effect(k: 'heart' | 'poof', x: number, y: number, z: number): void }): boolean {
    const f = FOODS.get(item);
    if (!f || !this.alive) return false;
    let used = false;
    if (this.health < this.maxHealth && f.heal > 0) { this.health = Math.min(this.maxHealth, this.health + f.heal); used = true; }
    if (this.baby) {
      this.growTicks = Math.max(1, this.growTicks - Math.floor(this.growTicks * 0.1));
      used = true;
    } else if (!this.tame && this.temper < MAX_TEMPER) {
      this.temper = Math.min(MAX_TEMPER, this.temper + f.temper); used = true;
    }
    if (f.love && this.tame && this.canFallInLove) { this.loveTicks = 600; used = true; }
    if (used) ctx?.effect(this.tame ? 'heart' : 'poof', this.x, this.y + this.height, this.z);
    return used;
  }

  /** The untamed horse's decision, once per ride attempt: roll 0..99 < temper tames it, else temper +5 (cap 100). True = tamed. */
  tameRoll(rnd: () => number): boolean {
    if (Math.floor(rnd() * MAX_TEMPER) < this.temper) { this.tame = true; return true; }
    this.temper = Math.min(MAX_TEMPER, this.temper + 5);
    return false;
  }

  protected override saveMore(o: Record<string, number>): void {
    o.t = this.tame ? 1 : 0; o.tp = this.temper; o.co = this.coat; o.mk = this.markings;
    o.js = this.jumpStrength; o.sp = this.hSpeed; o.mh = this.maxHealth; o.ar = this.armorTier; o.sd = this.saddled ? 1 : 0;
  }
  protected override loadMore(o: Record<string, number>): void {
    this.tame = !!o.t; this.temper = o.tp | 0; this.coat = (o.co | 0) % 7; this.markings = (o.mk | 0) % 5;
    if (o.js > 0) this.jumpStrength = o.js;
    if (o.sp > 0) this.hSpeed = o.sp;
    if (o.mh > 0) this.setMaxHealth(o.mh);
    this.armorTier = Math.max(0, Math.min(4, o.ar | 0)); this.saddled = !!o.sd;
    this.syncLook();
  }

  loot(rnd: () => number): Stack[] {
    const out: Stack[] = [];
    const l = this.baby ? 0 : Math.floor(rnd() * 3);
    if (l > 0) out.push(stackOf(I.LEATHER, l));
    if (this.saddled) out.push(stackOf(I.SADDLE, 1));
    if (this.armorTier > 0) out.push(stackOf(HORSE_ARMOR[this.armorTier], 1));
    return out;
  }
  override xpValue(rnd: () => number): number { return this.baby ? 0 : 1 + Math.floor(rnd() * 3); }

  protected override ai(ctx: MobCtx): void {
    super.ai(ctx);
  }
}

/** Factory for the manager's default branch. */
export function createRideMob(type: string, id: number, x: number, y: number, z: number, rnd: () => number): Horse | null {
  if (type === 'horse' || type === 'donkey') return new Horse(id, x, y, z, rnd, type);
  return null;
}

/** Plains / savanna herds: 2-6 horses, an eighth of them donkeys (vanilla weights 5 horse : 1 donkey). */
export function horseHerd(roll: number, biome: string): { type: 'horse' | 'donkey'; count: number } | null {
  if (!/plains|savanna/.test(biome) || biome.includes('snow') || biome.includes('ice')) return null;
  if (roll >= 0.35) return null;
  return { type: roll < 0.06 ? 'donkey' : 'horse', count: 2 + Math.floor((roll * 1000) % 5) };
}
